/**
 * Modal — the portal's dialog shell.
 *
 * Pairs the visual shell with useModalBehavior, so every dialog gets Escape,
 * focus trapping and background scroll lock for free. Before this, none of the
 * portal's eight overlays did any of that, and two hand-rolled Escape listeners
 * competed with each other.
 *
 * Rendered through a portal into <body> so a dialog is never clipped by an
 * ancestor's overflow or transform. While it is open the app behind it is
 * inert, and so is a dialog under it (modal-layers.ts): that is what makes it
 * modal, and it sets no aria-modal, which on Apple's and Android's
 * accessibility APIs pruned the toasts with the app, so VoiceOver heard none
 * while a dialog was open. design:check holds it (aria-modal-true).
 *
 * Usage:
 *
 *     <Modal
 *       isOpen={open}
 *       onClose={close}
 *       title="Delete cohort"
 *       footer={
 *         <>
 *           <Button variant="ghost" onClick={close}>Cancel</Button>
 *           <Button variant="danger" onClick={remove}>Delete</Button>
 *         </>
 *       }
 *     >
 *       <p>…</p>
 *     </Modal>
 *
 * The footer is a prop rather than a child so it stays pinned below the
 * scrollable body instead of scrolling away with the content.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { abovePage, openLayer } from './modal-layers';
import { useModalBehavior } from './use-modal-behavior';
import styles from './modal.module.css';

type ModalSize = 'sm' | 'md' | 'mdWide' | 'lg' | 'xl';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  size?: ModalSize;
  /** Gold double-line frame, matching Card — prestige/celebration dialogs. */
  framed?: boolean;
  /** Raise above ordinary modals. Confirmation dialogs opened from a modal. */
  layer?: 'modal' | 'confirm';
  /** Hide the header close button; Escape and the backdrop still dismiss. */
  hideClose?: boolean;
  /** Block dismissal by backdrop click — destructive confirmations. */
  disableBackdropClose?: boolean;
  closeLabel?: string;
  /** Action row, pinned below the scrollable body. */
  footer?: ReactNode;
  className?: string;
  children?: ReactNode;
}

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  framed = false,
  layer = 'modal',
  hideClose = false,
  disableBackdropClose = false,
  closeLabel = 'Close',
  footer,
  className,
  children,
}: ModalProps) {
  const overlay = useRef<HTMLDivElement>(null);
  // While it is open the app behind is inert, and so is a dialog under it.
  // Before the behaviour hook, on purpose: effects clean up in the order they
  // were declared, so as it closes the app is live again before the hook hands
  // the focus back into it — an inert element cannot take focus.
  useEffect(() => {
    if (!isOpen) return;
    return openLayer(overlay.current);
  }, [isOpen]);
  const behaviourRef = useModalBehavior<HTMLDivElement>({ isOpen, onDismiss: onClose });
  // The overlay, for the layers and for the behaviour hook alike: both read
  // its rung (a confirmation's over a dialog's), so the dialog on top for
  // the one is on top for the other.
  const placeOverlay = useCallback(
    (element: HTMLDivElement | null) => {
      overlay.current = element;
      behaviourRef.current = element;
    },
    [behaviourRef],
  );

  if (!isOpen) return null;

  const overlayClass = [styles.overlay, layer === 'confirm' ? styles.overlayConfirm : null]
    .filter(Boolean)
    .join(' ');

  const dialogClass = [
    styles.dialog,
    size !== 'md' ? styles[size] : null,
    framed ? styles.framed : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return abovePage(
    <div
      ref={placeOverlay}
      className={overlayClass}
      // Only a click that both starts and ends on the backdrop dismisses, so a
      // drag that began inside the dialog (selecting text) does not close it.
      onMouseDown={(event) => {
        if (disableBackdropClose) return;
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={dialogClass} role="dialog">
        {(title || !hideClose) && (
          <div className={styles.header}>
            <div>
              {title && <h2 className={styles.title}>{title}</h2>}
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
            {!hideClose && (
              <button type="button" className={styles.close} onClick={onClose} aria-label={closeLabel}>
                ×
              </button>
            )}
          </div>
        )}
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
  );
}
